import { createSelector } from '@reduxjs/toolkit';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { orderBy, uniq } from 'lodash';
import {
  AllNotificationSettings,
  FamilyMemberAllNotificationSettings,
  FamilyMemberNotificationTopicSettingResponse,
  SelfNotificationTopicSettingResponse,
} from 'mcsclient/models';

import { INotificationSettingResubscribeStatus, INotificationsState, NotificationTopicSettingAggregate } from './notifications.types';

/**
 * Aggregate a list of topic settings into a {@link NotificationTopicSettingAggregate}
 *
 * @param topicSettings
 */
const aggregateTopicSettings = (
  topicSettings: { isEnabled: boolean; disabledReason?: string; topicShortName: string }[]
): NotificationTopicSettingAggregate => {
  // topics that are `isEnabled` and have no `disabledReason`
  const enabledTopics = topicSettings.filter((topicSetting) => topicSetting.isEnabled && !topicSetting.disabledReason);

  return {
    isEnabled: enabledTopics.length > 0,
    isToggleable: topicSettings.some((topicSetting) => !topicSetting.disabledReason), // if some setting has no `disabledReason`
    enabledTopicShortNames: orderBy(
      // remove duplicates (applicable for family member when user subscribed to push and email for the same feature)
      uniq(enabledTopics.map((topicSetting) => topicSetting.topicShortName)),
      // order by the index of the topic setting in the original list
      (shortName) => topicSettings.findIndex((topicSetting) => topicSetting.topicShortName === shortName)
    ),
    areAllTopicsEnabled: enabledTopics.length === topicSettings.length && enabledTopics.length > 0,
  };
};

/**
 * Aggregate the topic settings in a {@link FamilyMemberAllNotificationSettings} into a {@link NotificationTopicSettingAggregate}
 *
 * @param familyMemberNotificationSetting
 */
const aggregateFamilyMemberTopicSettings = (
  familyMemberNotificationSetting: FamilyMemberAllNotificationSettings
): NotificationTopicSettingAggregate =>
  aggregateTopicSettings([
    ...familyMemberNotificationSetting.emailNotifications,
    ...familyMemberNotificationSetting.pushNotifications,
  ]);

/**
 * Aggregate a list of family member settings
 *
 * @param familyMemberSettings
 */
const aggregateFamilyMemberSettings = (
  familyMemberSettings: NotificationTopicSettingAggregate[]
): { isEnabled: boolean; isToggleable: boolean } => ({
  isEnabled: familyMemberSettings.some((familyMemberSetting) => familyMemberSetting.isEnabled),
  isToggleable: familyMemberSettings.some((familyMemberSetting) => familyMemberSetting.isToggleable),
});

export const getNotificationSettingsSummary: (state: INotificationsState) =>
  | (AllNotificationSettings & {
      pushNotificationSetting: NotificationTopicSettingAggregate;
      emailNotificationSetting: NotificationTopicSettingAggregate;
      familyNotificationSetting?: { isEnabled: boolean; isToggleable: boolean };
    })
  | undefined = createSelector([(state: INotificationsState) => state.notificationSettingsSummary], (notificationSettingsSummary) =>
  notificationSettingsSummary
    ? {
        ...notificationSettingsSummary,
        // augment AllNotificationSettings with push/email aggregate toggles based on the underlying topic settings
        pushNotificationSetting: aggregateTopicSettings(notificationSettingsSummary?.pushNotifications),
        emailNotificationSetting: aggregateTopicSettings(notificationSettingsSummary?.emailNotifications),
        // augment AllNotificationSettings with familyNotificationSetting aggregate toggle based on the underlying family member settings
        familyNotificationSetting: notificationSettingsSummary?.familyMemberNotificationSettings
          ? aggregateFamilyMemberSettings(
              notificationSettingsSummary.familyMemberNotificationSettings.map(aggregateFamilyMemberTopicSettings)
            )
          : undefined,
      }
    : undefined
);

export const getFamilyMemberNotificationSetting: (
  state: INotificationsState,
  puid: string
) => (FamilyMemberAllNotificationSettings & { notificationSetting: NotificationTopicSettingAggregate }) | undefined = createSelector(
  [
    (state: INotificationsState) => state.notificationSettingsSummary?.familyMemberNotificationSettings,
    (state: INotificationsState, puid: string) => puid,
  ],
  (familyMemberNotificationSettings: FamilyMemberAllNotificationSettings[] | undefined, puid: string) => {
    const familyMemberNotificationSetting = familyMemberNotificationSettings?.find((setting) =>
      localeIgnoreCaseEquals(setting.memberUserId, puid)
    );
    return familyMemberNotificationSetting
      ? {
          ...familyMemberNotificationSetting,
          // augment FamilyMemberAllNotificationSettings with an aggregate toggle based on the underlying topic settings
          notificationSetting: aggregateFamilyMemberTopicSettings(familyMemberNotificationSetting),
        }
      : undefined;
  }
);

export const getFamilyMemberPushNotificationTopicSetting: (
  state: INotificationsState,
  puid: string,
  topicId: string
) => FamilyMemberNotificationTopicSettingResponse | undefined = createSelector(
  [getFamilyMemberNotificationSetting, (state: INotificationsState, puid: string, topicId: string) => topicId],
  (familyMemberNotificationSetting: FamilyMemberAllNotificationSettings | undefined, topicId: string) =>
    familyMemberNotificationSetting?.pushNotifications.find((setting) => localeIgnoreCaseEquals(setting.topicId, topicId))
);

export const getFamilyMemberEmailNotificationTopicSetting: (
  state: INotificationsState,
  puid: string,
  topicId: string
) => FamilyMemberNotificationTopicSettingResponse | undefined = createSelector(
  [getFamilyMemberNotificationSetting, (state: INotificationsState, puid: string, topicId: string) => topicId],
  (familyMemberNotificationSetting: FamilyMemberAllNotificationSettings | undefined, topicId: string) =>
    familyMemberNotificationSetting?.emailNotifications.find((setting) => localeIgnoreCaseEquals(setting.topicId, topicId))
);

export const getSelfEmailNotificationTopicSetting: (
  state: INotificationsState,
  topicId: string
) => SelfNotificationTopicSettingResponse | undefined = createSelector(
  [
    (state: INotificationsState) => state.notificationSettingsSummary?.emailNotifications,
    (state: INotificationsState, topicId: string) => topicId,
  ],
  (emailNotifications: SelfNotificationTopicSettingResponse[] | undefined, topicId: string) =>
    emailNotifications?.find((setting) => localeIgnoreCaseEquals(setting.topicId, topicId))
);

export const getSelfPushNotificationTopicSetting: (
  state: INotificationsState,
  topicId: string
) => SelfNotificationTopicSettingResponse | undefined = createSelector(
  [
    (state: INotificationsState) => state.notificationSettingsSummary?.pushNotifications,
    (state: INotificationsState, topicId: string) => topicId,
  ],
  (pushNotifications: SelfNotificationTopicSettingResponse[] | undefined, topicId: string) =>
    pushNotifications?.find((setting) => localeIgnoreCaseEquals(setting.topicId, topicId))
);

export const getNotificationSettingResubscribeStatus: (
  state: INotificationsState
) => INotificationSettingResubscribeStatus | undefined = createSelector(
  [(state: INotificationsState) => state.notificationSettingResubscribeStatus],
  (resubscribeStatus) => resubscribeStatus
);
